const SITE_NAME = 'UMKM Indonesia'

export type ProductSeoInput = {
  name: string
  description?: string | null
  images?: string[] | null
  price?: number | string
  discountPrice?: number | string | null
}

export type ProductSeoBrand = {
  name: string
  slug: string
}

export function getProductPageUrl(brandSlug: string, productSlug: string) {
  return `${window.location.origin}/catalog/${brandSlug}/${productSlug}`
}

export function buildProductTitle(productName: string, brandName: string) {
  return `${productName} | ${brandName} | ${SITE_NAME}`
}

export function buildProductDescription(
  product: Pick<ProductSeoInput, 'name' | 'description'>,
  brand: Pick<ProductSeoBrand, 'name'>
) {
  if (product.description?.trim()) {
    return product.description.trim().slice(0, 160)
  }
  return `${product.name} dari ${brand.name}. Lihat detail produk dan hubungi penjual melalui ${SITE_NAME}.`.slice(
    0,
    160
  )
}

function resolveImageUrl(url?: string | null) {
  if (!url) return undefined
  if (url.startsWith('http')) return url
  return `${window.location.origin}${url.startsWith('/') ? url : `/${url}`}`
}

export function buildProductJsonLd(
  product: ProductSeoInput,
  brand: ProductSeoBrand,
  productSlug: string
) {
  const pageUrl = getProductPageUrl(brand.slug, productSlug)
  const image = product.images?.[0] ? resolveImageUrl(product.images[0]) : undefined

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: buildProductDescription(product, brand),
    brand: {
      '@type': 'Brand',
      name: brand.name,
    },
    url: pageUrl,
  }

  if (image) schema.image = image

  const price = Number(product.discountPrice ?? product.price)
  if (!Number.isNaN(price) && price > 0) {
    schema.offers = {
      '@type': 'Offer',
      price: String(price),
      priceCurrency: 'IDR',
      availability: 'https://schema.org/InStock',
      url: pageUrl,
    }
  }

  return schema
}

function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.content = content
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.rel = 'canonical'
    document.head.appendChild(el)
  }
  el.href = href
}

function setJsonLd(data: Record<string, unknown>) {
  const id = 'product-json-ld'
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.id = id
    el.type = 'application/ld+json'
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function applyProductSeo(
  product: ProductSeoInput,
  brand: ProductSeoBrand,
  productSlug: string,
  seoEnabled: boolean
) {
  const pageUrl = getProductPageUrl(brand.slug, productSlug)
  const title = buildProductTitle(product.name, brand.name)
  const description = buildProductDescription(product, brand)
  const image =
    resolveImageUrl(product.images?.[0]) ||
    `${window.location.origin}/vite.svg`

  document.title = title
  setCanonical(pageUrl)
  setMeta('name', 'description', description)
  setMeta('name', 'robots', seoEnabled ? 'index, follow' : 'noindex, follow')
  setMeta('property', 'og:type', 'product')
  setMeta('property', 'og:site_name', SITE_NAME)
  setMeta('property', 'og:title', product.name)
  setMeta('property', 'og:description', description)
  setMeta('property', 'og:url', pageUrl)
  setMeta('property', 'og:image', image)
  setMeta('name', 'twitter:card', 'summary_large_image')
  setMeta('name', 'twitter:title', product.name)
  setMeta('name', 'twitter:description', description)
  setMeta('name', 'twitter:image', image)
  setJsonLd(buildProductJsonLd(product, brand, productSlug))
}

export function clearProductSeo() {
  document.title = `${SITE_NAME} - Katalog Produk UMKM`
  document.getElementById('product-json-ld')?.remove()
}
